import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import '@ant-design/v5-patch-for-react-19';
import { App as AntdApp } from 'antd';
import React, { useEffect } from 'react';
import defaultSettings from '../config/defaultSettings';
import { adminCurrentUser } from './services/apis/admin';
import { errorConfig } from './requestErrorConfig';
import { getApiBaseUrl } from './utils/apiBase';
import { getAdminToken, removeAdminToken } from './utils/adminToken';
import { setAppApis } from './utils/antdApp';

export interface HjrxCurrentUser {
  id?: string;
  username?: string;
  name?: string;
  display_name?: string;
  avatar?: string;
  role?: string;
  access?: string;
}

const loginPath = '/user/login';
const adminHomeHref = process.env.NODE_ENV === 'development' ? '/hj/products' : '/#/hj/products';

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: HjrxCurrentUser;
  fetchUserInfo?: () => Promise<HjrxCurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    const adminToken = getAdminToken();
    if (!adminToken) {
      return undefined;
    }

    try {
      const currentUser = await adminCurrentUser({ skipErrorHandler: true });
      return currentUser as HjrxCurrentUser;
    } catch (_error) {
      removeAdminToken();
      return undefined;
    }
  };

  const { location } = history;
  if (location.pathname !== loginPath) {
    const currentUser = await fetchUserInfo();
    if (!currentUser) {
      history.push(loginPath);
    }
    return {
      currentUser,
      fetchUserInfo,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  return {
    actionsRender: false,
    avatarProps: {
      title:
        initialState?.currentUser?.display_name ||
        initialState?.currentUser?.name ||
        initialState?.currentUser?.username ||
        'HJRX',
    },
    footerRender: false,
    logoHref: adminHomeHref,
    logoTarget: '_self',
    menuHeaderRender: undefined,
    onPageChange: () => {
      const { location } = history;
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    childrenRender: (children) => {
      const Bridge: React.FC = () => {
        const app = AntdApp.useApp();
        useEffect(() => {
          setAppApis(app as any);
        }, [app]);
        return null;
      };

      return (
        <AntdApp>
          <Bridge />
          {children}
        </AntdApp>
      );
    },
    ...initialState?.settings,
  };
};

export const request: RequestConfig = {
  baseURL: getApiBaseUrl(),
  ...errorConfig,
};
